/**
 * The investigation watch model — what a case file is *following*.
 *
 * An investigation is two things at once: a **watch scope** (this module) and a
 * collaborative **case file** (the `@authe/workspace` shell on the detail page).
 * A {@link Watch} is one thing being followed — a protocol, an account, a
 * catalog row, a chain — and an investigation holds several, so "the Uniswap
 * insider" (their address *and* the pools they touch) is one case rather than
 * three.
 *
 * Watches are **multi-chain by construction**: chains are CAIP-2 (`eip155:1`)
 * and accounts CAIP-10 (`eip155:1:0xabc…`), matching what the backend now
 * stores, and a protocol or account watch spans every chain unless narrowed
 * because a hacker moving funds does not stay on one chain.
 *
 * Each watch resolves two ways, and both must agree or the feed contradicts
 * itself as rows arrive:
 *
 * - {@link watchToSearchTerm} — the backlog, as one term of a
 *   `POST /transactions/search` request. The server routes each term to the index
 *   that can answer it and merges the lanes behind one cursor, so the whole watch
 *   set is a single request with a single page budget.
 * - {@link matchesWatch} — the live tail, as a predicate over the published
 *   stream payload. AppSync has no server-side filtering, so every subscriber
 *   receives every row its channels carry and discards the non-matching ones
 *   client-side.
 */
import type { PoisoningPolicy } from "@authe/tx-table";
import { chainDisplayName, isEVM, normalizeAddress, toStoredChainId, shortenAddress } from "@authe/fe-commons/chains";
import {
  PRESET_LABELS,
  PRESET_SECONDS,
  type TimeRangePreset,
} from "@authe/fe-commons/metadata-analytics";
import { INVESTIGATION_CONTRACT, type SceneOverlayDTO } from "@authe/fe-commons/models";
import type { TxTenancyScopeId } from "@/features/transactions/scopes";
import type { CaseAct } from "./caseActs";

/** A CAIP-2 chain id, e.g. `eip155:1`. */
export type Caip2 = string;
/** A CAIP-10 account, e.g. `eip155:1:0xabc…`. */
export type Caip10 = string;

/** Catalog kinds a watch can follow (the reverse-index lane on the list API). */
export type WatchCatalogKind = "entity" | "tag" | "label" | "group";

/**
 * One thing an investigation follows.
 *
 * `chains` narrows a watch to specific chains and means the same thing on every
 * kind that carries it: **empty or absent is "do not narrow"**, not "no chains".
 * For a protocol that is every chain it is deployed on; for an account it is
 * every chain in the account's CAIP-2 namespace. Uniform because a scope editor
 * that means "all" in one place and "none" in another is a footgun, and because
 * `watchKey` derives identity from it.
 */
/**
 * The part an analyst says an account plays in THIS case — a claim made in the
 * case by a person, never a detector finding (which is why it is not the
 * finding subject's `accused`). Mirrors `CASE_ACTOR_ROLES` in
 * `commons.interfaces.models.investigation`.
 */
export const CASE_ACTOR_ROLES = ["attacker", "victim", "intermediary", "service", "bystander"] as const;
export type CaseActorRole = (typeof CASE_ACTOR_ROLES)[number];
export const CASE_ACTOR_ROLE_LABELS: Record<CaseActorRole, string> = {
  attacker: "Attacker",
  victim: "Victim",
  intermediary: "Intermediary",
  service: "Service / exchange",
  bystander: "Bystander",
};
export function isCaseActorRole(value: unknown): value is CaseActorRole {
  return typeof value === "string" && (CASE_ACTOR_ROLES as readonly string[]).includes(value);
}

export type Watch = (
  | { kind: "protocol"; protocolId: string; chains?: Caip2[] }
  | { kind: "address"; account: Caip10; chains?: Caip2[]; role?: CaseActorRole }
  | { kind: WatchCatalogKind; catalogId: string; name?: string }
  | { kind: "chain"; chain: Caip2 }
) & {
  /**
   * The server's stable key for a PERSISTED watch (`watch_key` on the wire).
   *
   * Absent on a watch the editor has just built — the server spells it on
   * write. The feed joins each search lane's row count and error back to the
   * watch that asked for it, and this is the spelling the server joins on;
   * {@link watchKey} is the client's derivation of the same thing, pinned to
   * the server's samples in `INVESTIGATION_CONTRACT.watch_key_samples`.
   */
  key?: string;
};

export type WatchKind = Watch["kind"];

/**
 * Whether two watches say the same thing — the same identity (`watchKey`) AND
 * the same words on it. A role or a catalog name is not part of a watch's
 * identity, so a diff by key alone read "Attacker" set on an existing account
 * as no change and never sent it.
 */
export function watchesEqual(a: Watch, b: Watch): boolean {
  if (watchKey(a) !== watchKey(b)) return false;
  if (a.kind === "address" && b.kind === "address") return (a.role ?? null) === (b.role ?? null);
  if ("name" in a && "name" in b) return (a.name ?? null) === (b.name ?? null);
  return true;
}

/**
 * The entity this case is about, when it is about one.
 *
 * The Entity / org watch template seeds exactly one entity watch, and in that
 * case nearly every attribution a team makes is "this too belongs to them" — so
 * the surfaces that attribute things offer it as a single click rather than a
 * search. A case following two entities has no such default and gets none: the
 * first of two would be a guess, and a wrong one-click attribution is worse than
 * a picker.
 */
export function watchedEntity(watches: Watch[]): WatchedEntity | undefined {
  const entities = watches.filter((w) => w.kind === "entity");
  if (entities.length !== 1) return undefined;
  const [only] = entities;
  if (!only || only.kind !== "entity") return undefined;
  return { id: only.catalogId, name: only.name ?? only.catalogId };
}

export interface WatchedEntity {
  id: string;
  name: string;
}

/**
 * What a case is ABOUT, derived from what it follows.
 *
 * The gallery's templates are a starting point, not a property of the case: the
 * created investigation keeps the watches and the saga preset and forgets which
 * card seeded them. That is the right way round — a case drifts (a protocol
 * monitor grows two addresses and an entity as the work develops), and a stored
 * template id would then be a claim about the case that is no longer true. The
 * watches are always the truth, so the focus is read from them each time.
 *
 * Three surfaces key off this: which columns a case opens with, which metrics
 * sit above its table, and which faceted filters its toolbar offers.
 */
export type CaseFocus =
  | { kind: "protocol"; protocolIds: string[] }
  | { kind: "entity"; entity: WatchedEntity }
  | { kind: "address"; accounts: Caip10[] }
  | { kind: "saga"; saga: NonNullable<InvestigationSaga> }
  | { kind: "mixed" };

/**
 * Read a case's focus from its watches (and its saga preset, which outranks
 * them — a bridge case is about whether both legs landed, whatever it follows
 * to find them).
 *
 * A case is focused only when its watches name ONE kind of subject. Chain
 * watches don't count: a chain narrows a case rather than naming what it is
 * about, so "this protocol, on Base" is still a protocol case. Anything else is
 * `mixed`, which every consumer treats as "offer the neutral view" — the same
 * refusal to guess as {@link watchedEntity}, and for the same reason. A case
 * following a protocol AND two entities has no single best set of columns, and
 * picking one would quietly hide the columns the other half of the work needs.
 */
export function caseFocus(watches: Watch[], saga: InvestigationSaga = null): CaseFocus {
  if (saga) return { kind: "saga", saga };

  const subjects = watches.filter((w) => w.kind !== "chain");
  const kinds = new Set(subjects.map((w) => w.kind));
  if (kinds.size !== 1) return { kind: "mixed" };

  switch ([...kinds][0]) {
    case "protocol":
      return { kind: "protocol", protocolIds: distinct(subjects, (w) => (w.kind === "protocol" ? w.protocolId : undefined)) };
    case "address":
      return { kind: "address", accounts: distinct(subjects, (w) => (w.kind === "address" ? w.account : undefined)) };
    case "entity": {
      const entity = watchedEntity(watches);
      return entity ? { kind: "entity", entity } : { kind: "mixed" };
    }
    default:
      // A tag / label / group watch is a real subject, but nothing on the page
      // specialises for one yet, so it reads as the neutral view rather than
      // pretending otherwise.
      return { kind: "mixed" };
  }
}

function distinct(watches: Watch[], pick: (watch: Watch) => string | undefined): string[] {
  const out = new Set<string>();
  for (const watch of watches) {
    const value = pick(watch);
    if (value) out.add(value);
  }
  return [...out].sort();
}

/** The chains a watch is narrowed to, sorted, or `[]` for "every chain". */
export function watchChains(watch: Watch): Caip2[] {
  if (watch.kind === "chain") return [watch.chain];
  if (watch.kind === "protocol" || watch.kind === "address") {
    return [...(watch.chains ?? [])].sort();
  }
  return [];
}

/**
 * Stable identity for a watch — React keys, de-duplication when two templates
 * seed the same thing, and per-lane cursor scoping in the feed.
 */
export function watchKey(watch: Watch): string {
  switch (watch.kind) {
    case "protocol":
      return `protocol:${watch.protocolId}:${watchChains(watch).join(",")}`;
    case "address":
      // The chain narrowing is part of the identity, so following an account
      // namespace-wide and following it on one chain are two watches rather
      // than one silently overwriting the other in `withWatch`.
      return `address:${watch.account}:${watchChains(watch).join(",") || "ns"}`;
    case "chain":
      return `chain:${watch.chain}`;
    default:
      return `${watch.kind}:${watch.catalogId}`;
  }
}

/** Split a CAIP-10 account into its chain and address halves. */
export function parseAccount(account: Caip10): { chain: Caip2 | null; address: string } {
  const cut = account.lastIndexOf(":");
  if (cut <= 0) return { chain: null, address: account };
  return { chain: account.slice(0, cut), address: account.slice(cut + 1) };
}

/** Join a chain and address into a CAIP-10 account (chainless → bare address). */
export function toAccount(chain: Caip2 | null | undefined, address: string): Caip10 {
  const canonical = toStoredChainId(chain);
  return canonical ? `${canonical}:${address}` : address;
}

/** The namespace half of a CAIP-2 id (`eip155:1` → `eip155`). */
export function caip2Namespace(chain: Caip2 | null | undefined): string | null {
  if (!chain) return null;
  const cut = chain.indexOf(":");
  return cut > 0 ? chain.slice(0, cut) : chain;
}

/** Human label for a watch chip. */
export function watchLabel(watch: Watch): string {
  switch (watch.kind) {
    case "protocol":
      return watch.protocolId;
    case "address": {
      const { address } = parseAccount(watch.account);
      return shortenAddress(address);
    }
    case "chain":
      return chainDisplayName(watch.chain);
    default:
      return watch.name ?? watch.catalogId;
  }
}

/**
 * The chains a followed protocol or account covers, as a chip reads them.
 *
 * Three states: narrowed to one chain, narrowed to several, and not narrowed at
 * all. The last is only worth printing for an account — for a protocol "every
 * chain" is the default and saying so on every chip is noise.
 *
 * An un-narrowed account STORES `eip155:*`, and that is the wrong thing to
 * print: every chip beside it reads "Ethereum", so the one reading `eip155:*`
 * asks a compliance reader to know CAIP-2 before they can tell what the case
 * follows. Same rule the seed queue's `ChainTag` states — the canonical id is
 * what to persist and what to hang on the tooltip, never the label. The title
 * keeps it, and names the account's own chain, because the run panel's coverage
 * note turns on exactly that distinction: followed everywhere, ingested there.
 */
export function watchChainScope(watch: Watch): { text: string; title: string } | null {
  if (watch.kind !== "address" && watch.kind !== "protocol") return null;
  const chains = watchChains(watch);
  if (chains.length === 1) {
    const name = chainDisplayName(chains[0]);
    return { text: name, title: name };
  }
  if (chains.length > 1) {
    return { text: `${chains.length} chains`, title: chains.map(chainDisplayName).join(", ") };
  }
  if (watch.kind === "protocol") return null;
  const chain = parseAccount(watch.account).chain;
  const namespace = caip2Namespace(chain);
  if (!namespace) return null;
  // `eip155` is the only namespace with a name a reader knows better than its
  // token; anything else is left as the token rather than guessed at.
  const family = isEVM(chain) ? "EVM" : namespace;
  const anchor = chainDisplayName(chain) || chain;
  return {
    text: `Every ${family} chain`,
    title: `Followed on every chain of the ${namespace} namespace (${namespace}:*), not just ${anchor}.`,
  };
}

/**
 * One line naming a watch in a list — kind, label and chain scope.
 *
 * {@link watchKey} is an IDENTITY (React keys, de-duplication, per-lane cursor
 * scoping) and was being printed as the option text in the case's
 * warehouse-analysis picker, so the reader chose between
 * `address:eip155:1:0x9ae755d23fc948fe94c9364a2398fd508a2ab0d2:eip155:1` and
 * `address:eip155:42161:0x78ad0e0691a92d3e2e01cd0c60c0e8a33354faf9:ns` — the
 * chain spelled twice, the address never shortened, and the narrowing as a bare
 * `ns`. The chips two tabs away already read "Address 0x9ae755…ab0d2 ·
 * Ethereum" off the same watch.
 */
export function watchOptionLabel(watch: Watch): string {
  const head = `${WATCH_KIND_LABELS[watch.kind]} ${watchLabel(watch)}`;
  const scope = watchChainScope(watch);
  return scope ? `${head} · ${scope.text}` : head;
}

/**
 * How a case reads "where the money came from and went".
 *
 * Deliberately small, and every control only removes or reorders — nothing here
 * invents a number. The panel is a CLAIM about the money rather than a listing
 * ("half of what left went to an exchange" gets quoted), so each setting's
 * effect is reported beside the totals. A control that can silently shrink a
 * denominator is how an exposure figure stops meaning anything.
 */
export interface CaseFundsFlowSettings {
  /**
   * Counterparties the case has ruled out, excluded from BOTH directions.
   *
   * Lower-cased and chain-agnostic: the judgement is about who the address IS,
   * and that does not vary by deployment. For the counterparty that is not
   * really one — the subject's own second custody account, an exchange's
   * internal sweep address, a router that receives everything.
   */
  excluded: string[];
  /**
   * Categories held at the top of each side, in this order, whatever their size.
   *
   * Ordering by value answers "where did most of it go", which is the right
   * default and the wrong one for a case that is ABOUT a mixer: a single 40 USD
   * hop into Tornado is the finding, and it sorts last.
   */
  pinnedCategories: string[];
  /** Count only rows the platform could price. Off by default — see the panel. */
  pricedOnly: boolean;
}

export const EMPTY_FUNDS_FLOW_SETTINGS: CaseFundsFlowSettings = {
  excluded: [],
  pinnedCategories: [],
  pricedOnly: false,
};

/** True when nobody has changed anything — drives the "Reset" affordance. */
export function fundsFlowSettingsAreDefault(s: CaseFundsFlowSettings): boolean {
  return s.excluded.length === 0 && s.pinnedCategories.length === 0 && !s.pricedOnly;
}

export const WATCH_KIND_LABELS: Record<WatchKind, string> = {
  protocol: "Protocol",
  address: "Address",
  entity: "Entity",
  tag: "Tag",
  label: "Label",
  group: "Group",
  chain: "Chain",
};

// ---------------------------------------------------------------------------
// Backlog: watch → search term
// ---------------------------------------------------------------------------

/** One lane of a `POST /transactions/search` request. */
export interface SearchTerm {
  kind: WatchKind;
  value: string;
  chains?: Caip2[];
}

/**
 * The case contract's limits, read off the generated snapshot of the server's
 * own declarations (`INVESTIGATION_CONTRACT`, from
 * `commons.interfaces.models.investigation`) rather than typed here.
 *
 * Each was a copy once, right the day it was typed. A cap that drifts on one
 * side is a save that silently truncates on the other, so every surface that
 * renders, validates against or pads to one of these numbers reads it from
 * here, and here reads it from the generated contract.
 */
const { limits: CASE_LIMITS, patterns: CASE_PATTERNS } = INVESTIGATION_CONTRACT;

/** Watches a case may follow — the search API's `terms` cap. */
export const MAX_WATCHES: number = CASE_LIMITS.max_watches;
/** Chains one protocol or account watch may be narrowed to. */
export const MAX_WATCH_CHAINS: number = CASE_LIMITS.max_watch_chains;
/** Title length. */
export const MAX_TITLE: number = CASE_LIMITS.max_title;
/** Story acts a case may carry, and the length of each text field. */
export const MAX_ACTS: number = CASE_LIMITS.max_acts;
export const MAX_ACT_TITLE: number = CASE_LIMITS.max_act_title;
export const MAX_ACT_NOTE: number = CASE_LIMITS.max_act_note;
export const MAX_ACT_PHASE: number = CASE_LIMITS.max_act_phase;
/** An exhibit's reason and caption. */
export const MAX_EVIDENCE_NOTE: number = CASE_LIMITS.max_evidence_note;
export const MAX_EVIDENCE_LABEL: number = CASE_LIMITS.max_evidence_label;
/** Hide rules a case may switch on, and the shape of one id. */
export const MAX_HIDE_RULES: number = CASE_LIMITS.max_hide_rules;
export const HIDE_RULE_ID_PATTERN = new RegExp(CASE_PATTERNS.hide_rule_id);
/** The retention row budget's ceiling. */
export const MAX_RETENTION_ROWS: number = CASE_LIMITS.max_rows;

/**
 * Watches one search request can carry, matching the server's `terms` cap.
 *
 * The server caps it because each term is at least one index query per page, so
 * an unbounded list turns one request into a fan-out the caller never sees the
 * cost of. The number is mirrored here so the scope editor can stop at the cap
 * instead of letting someone build an investigation whose feed then 400s — the
 * feed is one request now, so exceeding the cap fails the whole thing rather than
 * just the extra lane. The same number as {@link MAX_WATCHES} by construction:
 * the model's cap IS the search API's, declared once there.
 */
export const MAX_SEARCH_TERMS: number = MAX_WATCHES;

/**
 * The `POST /transactions/search` term that fetches this watch's backlog.
 *
 * The kinds line up one-for-one with {@link Watch}, which is the point of the
 * endpoint taking terms rather than filters: the server routes each term to the
 * index that can answer it, so the client no longer has to know that a protocol
 * watch is a `protocol_ids` filter while a tag watch is a `catalog_kind` pair.
 *
 * Chain narrowing is passed through as-is; empty means "every chain", the same
 * convention {@link watchChains} uses.
 *
 * The address is normalised here rather than left to the server, even though the
 * server normalises too. {@link searchTermLaneKey} has to reproduce the key the
 * server reports each lane's status under, and the server derives it from the
 * *canonical* value — so a checksummed address stored on a watch would key the
 * response one way and the lane chips another, and the chips would read zero.
 */
export function watchToSearchTerm(watch: Watch): SearchTerm {
  const chains = watchChains(watch);
  const narrow = chains.length ? { chains } : {};
  switch (watch.kind) {
    case "protocol":
      return { kind: "protocol", value: watch.protocolId, ...narrow };
    case "address": {
      const { chain, address } = parseAccount(watch.account);
      return { kind: "address", value: normalizeAddress(address, chain), ...narrow };
    }
    case "chain":
      return { kind: "chain", value: watch.chain };
    default:
      return { kind: watch.kind, value: watch.catalogId };
  }
}

/**
 * The per-lane identity the search response reports status under.
 *
 * Mirrors `TransactionSearchTerm.lane_key` on the server, which is what scopes
 * each lane's cursor. Duplicated across the language boundary because the
 * response's `sources[]` is keyed by it and there is nothing else to join a
 * lane's row count and error back to the watch that asked for it. The format is
 * pinned by a test on both sides; if they drift, lanes render as zero-count
 * rather than failing loudly, so keep them together.
 *
 * Note this keys on the *canonical* value the server echoes back, not the raw
 * one sent — an address is lower-cased and a chain resolves to CAIP-2 on the way
 * in, so joining on the request's own spelling would miss.
 */
export function searchTermLaneKey(term: SearchTerm): string {
  return `${term.kind}:${term.value}:${[...(term.chains ?? [])].sort().join(",")}`;
}

// ---------------------------------------------------------------------------
// Live tail: watch → stream-payload predicate
// ---------------------------------------------------------------------------

/** The subset of a published transaction payload the predicates read. */
interface StreamedRow {
  chain_id?: unknown;
  primary_protocol_id?: unknown;
  protocol_ids?: unknown;
  primary_entity_id?: unknown;
  participant_summary?: unknown;
  from?: unknown;
  to?: unknown;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value ? value : null;
}

function strList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string" && !!v) : [];
}

/**
 * Addresses a *streamed* row is known to involve.
 *
 * The published payload carries `participant_summary` (role counts plus the two
 * primary addresses), not the full participant list that the persisted row's
 * discovery index is built from. So the live tail sees an account only in the
 * sender/recipient position, while the backlog query matches it in any role —
 * a log-level counterparty appears on the next refetch rather than instantly.
 * Narrowing this would need the participant list on the wire, which is exactly
 * the fat payload `_ROW_PUBLISH_FIELDS` exists to prevent.
 */
function rowAddresses(row: StreamedRow): string[] {
  const summary = (row.participant_summary ?? {}) as Record<string, unknown>;
  return [str(summary.primary_from), str(summary.primary_to), str(row.from), str(row.to)].filter(
    (a): a is string => a !== null,
  );
}

/**
 * Whether a live streamed row belongs in this watch's feed.
 *
 * Catalog watches (`tag` / `label` / `group`) are **not decidable from the
 * wire**: an assignment lives in its own index and is not published on the row,
 * so this returns `false` for them rather than guessing. Those lanes stay
 * backlog-driven and pick up new rows on the next poll — a false negative that
 * delays a row, instead of a false positive that puts someone else's
 * transaction into a shared case file.
 */
export function matchesWatch(row: StreamedRow, watch: Watch): boolean {
  switch (watch.kind) {
    case "protocol": {
      const ids = new Set([str(row.primary_protocol_id), ...strList(row.protocol_ids)]);
      if (!ids.has(watch.protocolId)) return false;
      const chains = watchChains(watch);
      if (!chains.length) return true;
      const chain = toStoredChainId(str(row.chain_id));
      return chain ? chains.includes(chain) : false;
    }
    case "address": {
      const { chain, address } = parseAccount(watch.account);
      const rowChain = toStoredChainId(str(row.chain_id));
      const chains = watchChains(watch);
      if (chains.length) {
        if (!rowChain || !chains.includes(rowChain)) return false;
      } else if (chain && caip2Namespace(rowChain) !== caip2Namespace(chain)) {
        // Un-narrowed means namespace-wide, not chain-agnostic: an EVM address is
        // the same string on every `eip155` chain, but the identical string on a
        // different namespace is a different account.
        return false;
      }
      const wanted = normalizeAddress(address, rowChain ?? chain);
      return rowAddresses(row).some((a) => normalizeAddress(a, rowChain ?? chain) === wanted);
    }
    case "chain":
      return toStoredChainId(str(row.chain_id)) === watch.chain;
    case "entity":
      // The `primary_entity_id` rollup IS on the wire; explicit assignments are
      // not, so an assignment-only match arrives with the backlog.
      return str(row.primary_entity_id) === watch.catalogId;
    default:
      return false;
  }
}

/** Whether any watch in the scope accepts this row (an empty scope matches nothing). */
export function matchesAnyWatch(row: StreamedRow, watches: readonly Watch[]): boolean {
  return watches.some((watch) => matchesWatch(row, watch));
}

/** Whether the live tail can decide *anything* for this scope. */
export function hasLiveDecidableWatch(watches: readonly Watch[]): boolean {
  return watches.some((w) => w.kind !== "tag" && w.kind !== "label" && w.kind !== "group");
}

/**
 * Whether any lane still depends on the backlog poll, however live the tail is.
 *
 * The inverse of {@link hasLiveDecidableWatch} is not this: a scope can hold
 * both an address watch the stream serves and a tag watch it can't, and the
 * mixed case is the one that matters — slowing the poll because "the stream has
 * it covered" would silently delay exactly the lanes the stream never covers.
 *
 * `tag` / `label` / `group` are here because an assignment isn't on the
 * published payload at all. `entity` is here because only the
 * `primary_entity_id` rollup is: an explicit assignment to an entity is
 * invisible to {@link matchesWatch} and arrives with the backlog.
 */
export function needsBacklogPoll(watches: readonly Watch[]): boolean {
  return watches.some(
    (w) => w.kind === "tag" || w.kind === "label" || w.kind === "group" || w.kind === "entity",
  );
}

// ---------------------------------------------------------------------------
// Retention
// ---------------------------------------------------------------------------

/**
 * The stretch of `block_timestamp` a feed covers.
 *
 * Four cases rather than one preset list, because they behave differently and
 * the difference is load-bearing:
 *
 * - `rolling` — open-ended at the top. New transactions keep arriving, so the
 *   live stream applies and the floor moves as time passes.
 * - `since` — a fixed incident start and a live end. Older evidence never ages
 *   out merely because the investigation remains open another week.
 * - `absolute` — a closed historical window. Its contents cannot change, which is
 *   what makes it safe (and necessary) to stop the live stream and the poll: a
 *   transaction from today does not belong in a view of last March.
 * - `all` — no bound at all. Distinct from a very long rolling window because
 *   there is no floor to push to the server, so the query reads the top of the
 *   partition and paging is the only way further back.
 *
 * Preset vocabulary is reused from `@authe/fe-commons/metadata-analytics` so the
 * feed and the analytics views name the same spans the same way. Its
 * `resolveTimeRange` is deliberately NOT reused: it always returns both bounds,
 * which cannot express `all`, and it would turn a rolling window into a closed
 * one — silently killing the live tail.
 */
export type FeedWindow =
  | { kind: "rolling"; preset: TimeRangePreset }
  | { kind: "since"; since: number }
  | { kind: "absolute"; since: number; until: number }
  | { kind: "all" };

export interface Retention {
  /** The stretch of block time the feed covers. */
  window: FeedWindow;
  /**
   * Cap on rows held from the live stream and the newest page, across the whole
   * feed rather than per chain or per watch.
   *
   * Pages the reader walked back to with "load older" sit on top of it (see
   * {@link effectiveRowCap}), because a button that fetches rows the trim then
   * discards is a button that does nothing. The newest page does NOT: it arrives
   * unasked, so it is what this cap is for.
   */
  maxRows: number;
  /**
   * Spend the cap round-robin across chains instead of strictly newest-first.
   *
   * Chains differ in throughput by orders of magnitude, so a strictly
   * newest-first cap is really a race: BNB at ~3s blocks fills the feed and the
   * Ethereum leg someone is actually waiting for never appears, even though the
   * case follows both. Round-robin gives each chain an equal slice of the cap and
   * hands back what a quiet chain does not use, so a single-chain feed is
   * unaffected and a busy chain is only ever throttled by the presence of
   * another one with something to show. See `balanceRowsByChain`.
   */
  balanceChains: boolean;
}

/**
 * Rolling spans offered in the picker rail, newest-first — the server's
 * `FeedWindowPreset` vocabulary, which is a subset of the analytics presets.
 * Typed against `TimeRangePreset` so a preset the server adds that the
 * analytics views do not know fails here rather than at render.
 */
export const FEED_WINDOW_PRESETS: readonly TimeRangePreset[] = INVESTIGATION_CONTRACT.feed_window_presets;

export const WINDOW_ALL: FeedWindow = { kind: "all" };

/** What a new case opens on, as the server would default it. */
export const DEFAULT_RETENTION: Retention = {
  window: { kind: "rolling", preset: INVESTIGATION_CONTRACT.default_retention.preset },
  maxRows: INVESTIGATION_CONTRACT.default_retention.max_rows,
  // On by default because it costs nothing on the common case: with one chain in
  // the feed it is exactly newest-first, and it only starts holding rows back
  // once a second chain has rows that would otherwise never be reached.
  balanceChains: INVESTIGATION_CONTRACT.default_retention.balance_chains,
};

/** A closed window cannot gain rows, so nothing should be polling or streaming it. */
export function isClosedWindow(window: FeedWindow): boolean {
  return window.kind === "absolute";
}

/** Human label for the window, for the picker trigger and the feed header. */
export function feedWindowLabel(window: FeedWindow, now: number = Date.now()): string {
  if (window.kind === "all") return "Everything";
  if (window.kind === "rolling") return PRESET_LABELS[window.preset];
  if (window.kind === "since") return `${WINDOW_DAY_WITH_YEAR.format(window.since * 1000)} UTC → live`;
  // The year when either end is not this year. "Mar 23 – Apr 30" over a 2022
  // incident read as this spring, and the Ronin case's window was four years
  // older than its label said.
  const thisYear = new Date(now).getUTCFullYear();
  const otherYear =
    new Date(window.since * 1000).getUTCFullYear() !== thisYear ||
    new Date(window.until * 1000).getUTCFullYear() !== thisYear;
  // Date-based ranges end either at midnight (legacy) or the last inclusive
  // second. Do not render a UTC date as the following day in the viewer's zone.
  if (window.since % 86_400 === 0 && [0, 86_399].includes(window.until % 86_400)) {
    const day = otherYear ? WINDOW_DAY_WITH_YEAR : WINDOW_DAY;
    const from = day.format(window.since * 1000);
    const to = day.format(window.until * 1000);
    return `${from === to ? from : `${from} – ${to}`} UTC`;
  }
  const fmt = otherYear ? WINDOW_FMT_WITH_YEAR : WINDOW_FMT;
  return `${fmt.format(window.since * 1000)} – ${fmt.format(window.until * 1000)} UTC`;
}

const WINDOW_DAY = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

const WINDOW_DAY_WITH_YEAR = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

const WINDOW_FMT = new Intl.DateTimeFormat(undefined, {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const WINDOW_FMT_WITH_YEAR = new Intl.DateTimeFormat(undefined, {
  timeZone: "UTC",
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/**
 * The window's bounds in Unix **seconds**, `null` for unbounded on that side.
 *
 * Seconds, and via one helper rather than arithmetic at each call site, because
 * the units are a live hazard: `block_timestamp` is seconds on the API but
 * milliseconds from some ephemeral providers, and handing a millisecond value to
 * `since` asks for everything after the year 55000 — an empty feed that reads as
 * no activity rather than as a bug.
 */
export function windowBoundsSeconds(
  window: FeedWindow,
  now: number = Date.now(),
): { since: number | null; until: number | null } {
  switch (window.kind) {
    case "all":
      return { since: null, until: null };
    case "absolute":
      return { since: window.since, until: window.until };
    case "since":
      return { since: window.since, until: null };
    case "rolling":
      return {
        since: Math.floor(now / 1000) - PRESET_SECONDS[window.preset],
        // Deliberately open: a rolling window's top edge is "whatever arrives
        // next". Pinning it to now would exclude every row that lands after the
        // request was built, which is most of what a live feed is for.
        until: null,
      };
  }
}

/** Oldest `block_timestamp` (ms) a row may carry, or `null` for no floor. */
export function windowStartMs(window: FeedWindow, now: number = Date.now()): number | null {
  const { since } = windowBoundsSeconds(window, now);
  return since === null ? null : since * 1000;
}

/** Newest `block_timestamp` (ms) a row may carry, or `null` for no ceiling. */
export function windowEndMs(window: FeedWindow, now: number = Date.now()): number | null {
  const { until } = windowBoundsSeconds(window, now);
  return until === null ? null : until * 1000;
}

/**
 * A fixed incident start must not slide away like a rolling window. An open
 * incident end stays live; only an explicitly closed period stops the tail.
 */
export function periodFeedWindow(
  period: CasePeriod | null,
  now: number = Date.now(),
): FeedWindow | null {
  if (!period || period.since === null) return null;
  if (period.until === null) return period.since <= now ? { kind: "since", since: Math.floor(period.since / 1000) } : null;
  const until = period.until ?? now;
  if (until < period.since) return null;
  return {
    kind: "absolute",
    since: Math.floor(period.since / 1000),
    until: Math.floor(until / 1000),
  };
}

/** Creation default only. Explicit reader-selected windows remain authoritative. */
export function initialCaseRetention(period: CasePeriod | null, retention?: Partial<Retention>): Retention {
  return { ...DEFAULT_RETENTION, ...retention, window: retention?.window ?? periodFeedWindow(period) ?? DEFAULT_RETENTION.window };
}

/**
 * True when the feed window and the case's period share no time at all.
 *
 * The period is what the case's runs INGEST; the window is what its table KEEPS.
 * They are separate settings on purpose (see {@link CasePeriod}), which is also
 * how they drift apart without anybody noticing: the Ronin case was given a
 * 2022 period on the Scope tab, its runs fetched that stretch, and the table
 * kept showing the last 7 days — its 13 held transactions and nothing else.
 * Because the feed was not EMPTY, the empty-feed rescue never appeared either.
 *
 * Disjoint only, never "does not contain": a window narrowed to one day INSIDE
 * the period is a reader zooming in, and nagging them to widen it back would be
 * answering a question they did not ask.
 */
export function windowMissesPeriod(
  window: FeedWindow,
  period: CasePeriod | null,
  now: number = Date.now(),
): boolean {
  if (!period || (period.since === null && period.until === null)) return false;
  const start = windowStartMs(window, now);
  const end = windowEndMs(window, now) ?? now;
  const periodUntil = period.until ?? now;
  if (start !== null && start > periodUntil) return true;
  if (period.since !== null && end < period.since) return true;
  return false;
}

/** A rolling view can overlap an ongoing incident while hiding its beginning. */
export function windowHidesIncidentStart(window: FeedWindow, period: CasePeriod | null, now = Date.now()): boolean {
  return window.kind === "rolling" && period?.since != null &&
    (windowStartMs(window, now) ?? 0) > period.since;
}

/**
 * The row cap to actually enforce, given how much history was explicitly loaded.
 *
 * `maxRows` bounds what the feed accumulates on its own — the live stream and the
 * newest page. It must not bound what the reader deliberately fetched: pressing
 * "load older" past the cap would drop the rows it just retrieved, so the button
 * would appear to do nothing and the count would sit still. Each loaded page
 * therefore buys room ON TOP of the retained head.
 *
 * `loadedOlder` counts only the pages walked back to. Counting the newest page
 * as well lets the head satisfy the cap by itself, and then the setting never
 * binds — the feed sits at whatever that page happened to return, which for a
 * multi-chain scope reads as the cap being applied per chain.
 */
export function effectiveRowCap(maxRows: number, loadedOlder: number): number {
  return maxRows + loadedOlder;
}

// ---------------------------------------------------------------------------
// Persisted investigation state (`SavedView.state.extra`)
// ---------------------------------------------------------------------------

export type InvestigationStatus = "open" | "archived";

export type { SceneOverlayDTO };

/**
 * The cross-chain saga kind a case's live monitor follows — `bridge.<family>`,
 * or `null` for a case that watches every bridge.
 *
 * A string, not a two-value union. Saga kinds are data: the grouping engine
 * forms them from protocol definitions and tenant rules, and `SAGA_LABELS`
 * (`@authe/fe-commons/grouping`) already names a dozen bridges. While this said
 * `"bridge.ccip" | "bridge.cctp"`, a case about any other bridge got no monitor
 * at all — and a Relay case carried a tab labelled CCIP that could never fill.
 * The backend validates the same shape (`SAGA_KIND_PATTERN`).
 */
export type InvestigationSaga = string | null;

/** Whether `value` is a saga kind a case's monitor can follow. */
export function isCaseSagaKind(value: unknown): value is string {
  return typeof value === "string" && /^bridge\.[a-z0-9_]+$/.test(value);
}

/**
 * The stretch of chain time the case is ABOUT.
 *
 * Deliberately not `retention.window`, which answers a different question — how
 * much of the live feed to keep on screen. The two were the same field in
 * practice, because the run planner had nothing else to read, and the result was
 * that a case's INGEST window was a display preference. A rolling "last 7 days"
 * is a sensible default for a table and structurally incapable of reaching a
 * historical incident, because it slides with today: on a real case, six runs
 * each succeeded and fetched nothing, with no error anywhere, because the window
 * resolved to ~63,000 blocks past the most recent transaction any of its
 * addresses had made.
 *
 * Milliseconds in the FE, seconds on the wire — the same convention as every
 * other timestamp here.
 *
 * Both bounds optional, and they mean different things: `since` alone is an
 * incident that is still unfolding (the common shape while a case is live), both
 * is a closed period, and `null` is nobody has said. That last one is NOT "all
 * of history" — the run planner reports the absence rather than inventing a
 * window, because for an address too large to ingest whole there is no safe
 * default to invent.
 */
export interface CasePeriod {
  since: number | null;
  until: number | null;
}

export interface InvestigationState {
  watches: Watch[];
  retention: Retention;
  /** What the case is ABOUT — see {@link CasePeriod}. `null` means unstated. */
  period: CasePeriod | null;
  /**
   * Which tenancy tier the feed reads from.
   *
   * NOT the same thing as who can see the case file — that is the saved view's
   * own `scope` (Personal / Shared). They were previously the same value, which
   * meant a shared case could only ever read the tenant partition. The tiers are
   * independent because the same transaction is stored once per tier: `user` and
   * `tenant` hold only what somebody imported there, while the ingest pipeline's
   * output lands in the shared `global` record — so a case following a protocol
   * or an entity across chains is nearly always asking about `global`.
   */
  feedScope: TxTenancyScopeId;
  status: InvestigationStatus;
  saga: InvestigationSaga;
  /**
   * The team's own reading of the graph: nodes the decode couldn't see, links
   * the chain doesn't record, relabels, and the noise they've hidden.
   *
   * Lives here rather than only in the workspace's Yjs doc because that doc is
   * a live cache — without a connected peer it survives neither a reload nor a
   * teammate opening the case tomorrow. Annotations are a conclusion, so they
   * belong to the case the same way its watches and pinned evidence do.
   *
   * Kept as the RAW field, deliberately: it holds one layer per SUBJECT, and a
   * case written before that carries a single flat body instead. Every reader
   * goes through `annotationLayers` / `layerForSubject`, which read both shapes,
   * so the migration is a read rule rather than a batch job — and a case nobody
   * opens keeps working untouched.
   */
  annotations: unknown;
  /**
   * Which table columns the case shows (`{ protocol: false }` — absent means
   * visible), or `null` while nobody has chosen and the focus default applies.
   *
   * Part of the case rather than the browser because a case is a team's shared
   * document: the columns someone put on screen to make an argument are part of
   * that argument, and a layout kept in `localStorage` means the teammate reading
   * the case tomorrow sees a different table than the one it was written against.
   * A reader without edit rights still gets to rearrange their own view — theirs
   * just stays local (see the detail page's `columnsShared`).
   */
  columnVisibility: Record<string, boolean> | null;
  /**
   * How the case reads its counterparty summary — see {@link CaseFundsFlowSettings}.
   *
   * On the case rather than in the browser for the reason `columnVisibility` is:
   * a case is a team's shared document, and "we ruled this address out" is a
   * conclusion about the investigation, not a per-reader preference.
   */
  fundsFlow: CaseFundsFlowSettings;
  /**
   * The hide rules switched on for this case (`["poisoning"]`), or `null` while
   * nobody has chosen — every rule off. Ids are `caseHideRules` entries.
   *
   * On the case for the reason {@link columnVisibility} is: the rows a case hid
   * to make its argument are part of that argument. A reader without edit
   * rights still toggles their own view; theirs stays in the session.
   */
  hideRules: readonly string[] | null;
  /**
   * How closely an address must resemble one the case paid to be called an
   * address-poisoning lookalike, or `null` for the default — the tenancy's
   * confidence policy (`forensic_checks.evm.address_poisoning.params`), else the
   * catalogue's (`DEFAULT_POISONING_POLICY`). Within the check's limits. On the
   * case for the reason `hideRules` is: the rows a case calls poisoning are part
   * of its argument. For viewing only: the catalog follows the policy.
   */
  poisoningPolicy: PoisoningPolicy | null;
  /**
   * The pipeline this case's ingest runs through, or `null` for the system default.
   *
   * On the case rather than chosen per run because it is a property of the
   * INVESTIGATION, not of one address: a case following six wallets wants all six
   * decoded the same way, and six runs authored separately is six chances to pick
   * differently. A case whose findings came from two decode paths is one whose
   * feed cannot be compared with itself.
   *
   * `null` is a real answer, not a missing one — most cases genuinely want the
   * platform default, and storing an id for it would pin them to a definition
   * that can be renamed or deleted underneath them.
   */
  pipelineId: string | null;
  /**
   * The case's narrative — what happened, in order.
   *
   * Ordered by the array, not by a timestamp on each act: the sequence IS the
   * claim, and several acts can share a block. Empty for a case nobody has
   * written up, which is most of them — the feed, graph and flows all work
   * without it, and a case that is a monitor rather than a story never needs one.
   *
   * See `./caseActs`, particularly why each act names its own chains.
   */
  acts: CaseAct[];
  /** Last time a teammate touched the case (not the last matching transaction). */
  lastActivityAt: number;
}

/**
 * The tenancy tier a run started from the dashboard writes its rows into.
 *
 * `PersistStep` writes the run's `TransactionRow` from the run's own
 * `TenantContext`, and `run_worker._run_tenancy` takes that from the caller's
 * snapshot — user included. So the row lands in the caller's tenant partition
 * (`{customer}#{org}#{tenant}`) under the RUNNER's user segment of the sort key:
 * a private copy, stored beside the tenant-shared `*` copy, never on top of it.
 *
 * transactions-mgmt-api serves a private copy to its owner alone, on the list
 * reads as on the detail read (`repository._served_copy`, which reuses the
 * detail read's `canonical_resolution`). That decides the tier:
 *
 *  - `user` — the caller's own view, `?scope=user` with their id stamped by
 *    `txScopeRequest`: their copy first, else the tenant-shared one. The run is
 *    here, for the runner.
 *  - `tenant` — `?scope=tenant` with the `user_id=*` sentinel, "as my tenant sees
 *    it": tenant-shared copies only. The run is NOT here, not even for the runner.
 *
 * It was `tenant` while the list reads still handed out every user's rows in the
 * partition — the same flaw that showed one colleague another's private run. A
 * dashboard run is now seen by its runner alone until sharing exists: a teammate
 * reading the same case at `user` gets their own view, which does not include it.
 *
 * Written down as a constant with its derivation because it is the single fact
 * behind the most expensive mistake on this path: a case reading any other tier
 * will not see one row of a run the user just paid for, and nothing errors.
 */
export const RUN_LANDS_IN: TxTenancyScopeId = "user";

export const DEFAULT_INVESTIGATION_STATE: InvestigationState = {
  watches: [],
  retention: DEFAULT_RETENTION,
  // Unstated, not "everything". A new case has not been told what it is about,
  // and a default here would be a guess the run planner then acts on.
  period: null,
  // The tier this platform's own ingest actually writes to, derived from the
  // same constant the run planner uses so the two cannot disagree.
  //
  // This was `global`, on the stated reasoning that "the shared global record is
  // where ingested transactions land". That is false for a run started from the
  // dashboard: `PersistHeaderStep` keys the row from the run's own
  // `TenantContext`, which is the CALLER's. Measured on a live case — 473 rows
  // for its six addresses in the caller's tenant partition, one in `*#main#main`
  // — the feed showed that one row, which reads as "the ingest barely found
  // anything" rather than as "you are looking at the wrong partition".
  //
  // `global` is still a legitimate choice, and the picker offers it: a case
  // following a protocol or entity that a system-wide sweep ingested belongs
  // there. It is just not what a case whose ingest you start yourself should
  // default to.
  feedScope: RUN_LANDS_IN,
  status: "open",
  saga: null,
  annotations: {},
  columnVisibility: null,
  fundsFlow: EMPTY_FUNDS_FLOW_SETTINGS,
  hideRules: null,
  poisoningPolicy: null,
  pipelineId: null,
  acts: [],
  lastActivityAt: 0,
};

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

/** Which editor a template opens with, so the gallery asks for the right thing. */
export type TemplateSeedKind =
  | "protocol"
  | "address"
  | "catalog"
  | "saga"
  | "blank"
  /**
   * Seeded from a published incident in `commons.incidents` — the one seed kind
   * that fills in the TITLE, the WATCHES and the RETENTION at once, because the
   * registry already knows all three. See `./incidentSeed`.
   */
  | "incident";

export interface InvestigationTemplate {
  id: string;
  title: string;
  /** One line naming the actual job, so the gallery reads as use cases. */
  description: string;
  seedKind: TemplateSeedKind;
  saga: InvestigationSaga;
  /** Suggested case title prefix. */
  titleHint: string;
  /** Windows longer than the default where the work is retrospective. */
  retention?: Partial<Retention>;
}

export const INVESTIGATION_TEMPLATES: readonly InvestigationTemplate[] = [
  {
    id: "protocol-monitor",
    title: "Protocol monitor",
    description: "Every transaction in and out of one or more protocols, as it lands.",
    seedKind: "protocol",
    saga: null,
    titleHint: "Protocol monitor",
  },
  {
    id: "address-trail",
    title: "Address trail",
    description:
      "Follow accounts across chains — funds moving after an exploit, or an insider's positions.",
    seedKind: "address",
    saga: null,
    titleHint: "Address trail",
    retention: { window: { kind: "rolling", preset: "7d" } },
  },
  {
    id: "entity-watch",
    title: "Entity / org watch",
    description: "Everything touching a catalog entity: your own addresses, or a counterparty's.",
    seedKind: "catalog",
    saga: null,
    titleHint: "Entity watch",
    retention: { window: { kind: "rolling", preset: "7d" } },
  },
  {
    id: "cross-chain-saga",
    title: "Cross-chain saga",
    description: "Watch both legs of bridged messages and catch the ones whose destination fails.",
    seedKind: "saga",
    saga: "bridge.ccip",
    titleHint: "CCIP sagas",
  },
  {
    id: "incident",
    title: "Published incident",
    description:
      "Start from a reported exploit — its addresses, dates and sources, already filled in.",
    seedKind: "incident",
    saga: null,
    titleHint: "Incident",
    // Deliberately absent: the retention comes from the incident's own date via
    // `incidentRetention`, which is the whole point of this template. A default
    // here would be a 24h window over an exploit from last month — an empty feed
    // and no clue why.
  },
  {
    id: "blank",
    title: "Blank case",
    description: "Start empty and add what you are following as you go.",
    seedKind: "blank",
    saga: null,
    titleHint: "Investigation",
  },
] as const;

export function investigationTemplate(id: string): InvestigationTemplate | null {
  return INVESTIGATION_TEMPLATES.find((t) => t.id === id) ?? null;
}
